import React, { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash.debounce';
import DataTable from '@salesforce/design-system-react/module/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/module/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/module/components/data-table/cell';
import DataTableInteractiveLink from '@salesforce/design-system-react/module/components/data-table/interactive-link';
import Icon from '@salesforce/design-system-react/module/components/icon';
import PageHeader from '@salesforce/design-system-react/module/components/page-header';
import { getDateTimeFromTimestamp } from '../../utilities/datetime';
import { useDispatch, useSelector } from 'react-redux';
import {
  createNotes,
  getSnapshots,
  updateNotes,
  snapshotsSelector,
} from '../../store/snapshots/slice';
import { setSyncTime } from '../../services/snapshot';
import { useNavigate } from 'react-router-dom';
import { getNumbersWithDecimalPlaces, getNumberWithCommas } from '../../utilities/money';
import { isEmpty } from '@firebase/util';
import { toast, Toaster } from 'react-hot-toast';
import Input from '@salesforce/design-system-react/components/input';
import InputIcon from '@salesforce/design-system-react/components/icon/input-icon';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';

const CustomDataTableCell = ({ children, ...props }) => {
  const navigate = useNavigate();

  function viewSnapshot(timestamp) {
    setSyncTime(timestamp);
    navigate('/home');
  }

  return (
    <DataTableCell title={getDateTimeFromTimestamp(children)} {...props}>
      <div style={{ fontWeight: 'bold' }} onClick={() => viewSnapshot(children)}>
        <DataTableInteractiveLink>View</DataTableInteractiveLink>
      </div>
    </DataTableCell>
  );
};
CustomDataTableCell.displayName = DataTableCell.displayName;

const CustomDataTableCellExport = ({ children, ...props }) => (
  <DataTableCell title={children} {...props}>
    <div style={{ fontWeight: 'bold' }}>Export</div>
  </DataTableCell>
);
CustomDataTableCellExport.displayName = DataTableCell.displayName;

const CustomDataTableNotes = ({ children, ...props }) => {
  const [editedValue, setEditedValue] = useState(children ? children.note : '');
  const dispatch = useDispatch();

  const addUpdateNotesHandler = (values) => {
    dispatch(values.id ? updateNotes(values) : createNotes(values));
  };

  const onCellEditData = (item, data) => {
    // setEditedValue(item);
    if (item === children.note) return;
    props.setShowToast(true);
    // if (!item) return;
    const values = {
      note: {
        note: item,
        entity_id: data?.item?._id,
        type: 'snapshot',
      },
      id: children ? children.id : null,
    };
    addUpdateNotesHandler(values);
  };

  return (
    <DataTableCell
      title={children?.note ? children.note : 'Add note'}
      {...props}
    >
      <IconSettings iconPath="/assets/icons">
        <Input
          iconLeft={
            <InputIcon
              assistiveText={{
                icon: 'Search',
              }}
              name="edit"
              category="utility"
            />
          }
          type="text"
          value={editedValue}
          placeholder="Add note"
          onChange={(e) => {
            setEditedValue(e.target.value);
          }}
          className={`editable-cell-input tablenote`}
          onBlur={(e) => onCellEditData(e.target.value, props)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onCellEditData(e.target.value, props);
            }
          }}
          styleInput={{border: '0px'}}
        />
      </IconSettings>
    </DataTableCell>
  );
};
CustomDataTableNotes.displayName = DataTableCell.displayName;

const CustomDataTableNetWorth = ({ children, ...props }) => {
  const { item } = props;
  const { portfoilo_networth } = item;
  const amount = getNumberWithCommas(getNumbersWithDecimalPlaces(portfoilo_networth, 2));

  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '400' }}>${amount}</span>
    </DataTableCell>
  );
};
CustomDataTableNetWorth.displayName = DataTableCell.displayName;

const CustomDataTableDate = ({ children, ...props }) => {
  return (
    <DataTableCell title=" " {...props}>
      <div>{getDateTimeFromTimestamp(children)}</div>
    </DataTableCell>
  );
};
CustomDataTableDate.displayName = DataTableCell.displayName;

const Snapshots = () => {
  const dispatch = useDispatch();
  const { snapshots, loading, pagination, errors, success, message } =
    useSelector(snapshotsSelector);
  const [sortColumn, setSortColumn] = useState('');
  const [sortColumnDirection, setSortColumnDirection] = useState('');
  const [paginated, setPaginated] = useState(0);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    dispatch(getSnapshots());
  }, [dispatch]);

  useEffect(() => {
    setHasMore(true);
    if (snapshots.length > 0) {
      setItems(snapshots);
    }
  }, [snapshots, dispatch]);

  const handleSort = (sortColumn, ...rest) => {
    const { property, sortDirection } = sortColumn;
    setSortColumn(property);
    setSortColumnDirection(sortDirection);
    setItems([]);
    setHasMore(true);
    setIsPageLoading(false);
    setPaginated(0);
    dispatch(getSnapshots({ sort_column: property, sort_order: sortDirection }));
  };


  const handleLoadMore = () => {
    if (!loading && !isPageLoading) {
      if (pagination?.hasNextPage) {
        setIsPageLoading(true);
        setPaginated(paginated + 1);
        setTimeout(() => {
          if (pagination.pages <= 1 || paginated >= pagination.pages || !pagination.hasNextPage) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }

          if (pagination.currentPage >= pagination.pages) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }
          let url = new URLSearchParams(pagination.nextPageUrl);

          dispatch(
            getSnapshots({
              limit: url.get('limit'),
              offset: url.get('offset'),
              q: '',
              sort_column: sortColumn,
              sort_order: sortColumnDirection,
            })
          );
          setIsPageLoading(false);
        }, 1000);
      } else {
        setIsPageLoading(false);
        setHasMore(false);
      }
    }
  };

  useEffect(() => {
    if (!showToast) return;
    success &&
      !loading &&
      toast.success(message, {
        id: 'snapshot-note-success',
        duration: 3000,
      });

    !success &&
      !loading &&
      !isEmpty(errors) &&
      toast.error(errors, {
        id: 'snapshot-note-error',
        duration: 3000,
      });
  }, [success, message, showToast, errors, loading]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      <div className="pageContent">
        <PageHeader
          icon={<Icon assistiveText={{ label: 'Snapshots' }} category="standard" name="account" />}
          info={(items.length || [].length) + ' item • Recently updated'}
          label="Snapshots"
          title="Snapshots"
          truncate
          variant="object-home"
        />

        <div style={{ height: `${snapshots.length === 0 ? '100px' : 'calc(100vh - 209px)'}` }}>
          <DataTable
            assistiveText={{
              actionsHeader: 'actions',
              columnSort: 'sort this column',
              columnSortedAscending: 'asc',
              columnSortedDescending: 'desc',
              selectAllRows: 'Select all rows',
              selectRow: 'Select this row',
            }}
            fixedHeader
            fixedLayout
            items={items}
            id="TransactionTable"
            onSort={handleSort}
            className="defi-transaction-dataTable"
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            noRowHover
          >
            <DataTableColumn
              isSorted={sortColumn === 'status'}
              label="Name"
              primaryColumn
              property="status"
              sortable
              width="10rem"
              sortDirection={sortColumnDirection.status}
            />
            <DataTableColumn
              isSorted={sortColumn === 'sync_time'}
              label="Date"
              property="sync_time"
              sortable
              width="10rem"
              sortDirection={sortColumnDirection.sync_time}
            >
              <CustomDataTableDate />
            </DataTableColumn>
            <DataTableColumn
              label="Portfolio Networth"
              property="portfoilo_networth"
              width="7.5rem"
            >
              <CustomDataTableNetWorth />
            </DataTableColumn>
            <DataTableColumn label="Notes" property="note" width="20rem">
              <CustomDataTableNotes setShowToast={setShowToast} />
            </DataTableColumn>
            <DataTableColumn label="Export" property="_id" width="7.5rem">
              <CustomDataTableCellExport />
            </DataTableColumn>
            <DataTableColumn label="View" property="sync_time" sortable width="7.5rem">
              <CustomDataTableCell />
            </DataTableColumn>
          </DataTable>
          {(snapshots.length === 0 || items.length === 0) && !hasMore && (
            <div className="nodatafound">
              <div>
                <img
                  src="/assets/empty-state/empty-folder.png"
                  alt=""
                  style={{ height: '150px' }}
                />
                <p style={{ textAlign: 'center', fontWeight: '700', fontSize: '20px' }}>
                  Data not available
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Snapshots;
